import { useMemo, useState } from 'react'
import {
  Box, Breadcrumbs, Typography, Stack, Card, CardContent, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, Chip, CircularProgress, Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material'
import { Schedule } from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useQuery } from '@tanstack/react-query'
import {
  getAssetDepreciationSummary, getAssetDepreciationSchedule, ASSET_CATEGORIES,
  type AssetDepreciationSummaryItem, type AssetCategory, type AssetStatus,
} from '../../api/active'
import { COST_CENTERS, type CostCenter } from '../../api/deconturi'

const STATUS_LABEL: Record<AssetStatus, string> = {
  InCursDePunereInFunctiune: 'În curs de PIF', Functional: 'Funcțional', InMentenanta: 'În mentenanță',
  InConservare: 'În conservare', Casat: 'Casat', Vandut: 'Vândut',
}
const CATEGORY_LABEL = Object.fromEntries(ASSET_CATEGORIES.map((c) => [c.value, c.label])) as Record<AssetCategory, string>
const COST_CENTER_LABEL = Object.fromEntries(COST_CENTERS.map((c) => [c.value, c.label])) as Record<CostCenter, string>

function monthLabel(dateStr: string) {
  const d = new Date(dateStr)
  return d.toLocaleDateString('ro-RO', { month: 'long', year: 'numeric' })
}

export default function AmortizarePage() {
  const [scheduleTarget, setScheduleTarget] = useState<AssetDepreciationSummaryItem | null>(null)
  const { data, isLoading } = useQuery({ queryKey: ['active', 'amortizare'], queryFn: getAssetDepreciationSummary })

  const columns = useMemo<GridColDef<AssetDepreciationSummaryItem>[]>(() => [
    { field: 'inventoryNumber', headerName: 'Nr. inventar', width: 140, renderCell: (p) => <code>{p.row.inventoryNumber}</code> },
    { field: 'name', headerName: 'Denumire', flex: 1, minWidth: 200 },
    { field: 'category', headerName: 'Categorie', width: 170, renderCell: (p) => CATEGORY_LABEL[p.row.category] },
    { field: 'costCenter', headerName: 'Centru de cost', width: 140, renderCell: (p) => COST_CENTER_LABEL[p.row.costCenter] },
    { field: 'entryValue', headerName: 'Val. intrare', width: 120, type: 'number', valueFormatter: (v: number) => v.toFixed(2).replace('.', ',') },
    { field: 'accumulatedDepreciation', headerName: 'Amortizare cumulată', width: 160, type: 'number', valueFormatter: (v: number) => v.toFixed(2).replace('.', ',') },
    { field: 'netBookValue', headerName: 'Val. netă contabilă', width: 150, type: 'number', valueFormatter: (v: number) => v.toFixed(2).replace('.', ',') },
    { field: 'currentPeriodDepreciation', headerName: 'Amortizare lună curentă', width: 170, type: 'number', valueFormatter: (v: number) => v.toFixed(2).replace('.', ',') },
    { field: 'yearToDateDepreciation', headerName: 'Amortizare an curent', width: 160, type: 'number', valueFormatter: (v: number) => v.toFixed(2).replace('.', ',') },
    {
      field: 'status', headerName: 'Status', width: 130, sortable: false,
      renderCell: (p) => <Chip size="small" label={STATUS_LABEL[p.row.status]} variant="outlined" />,
    },
    {
      field: 'actions', headerName: '', width: 56, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => (
        <IconButton size="small" title="Fișă de amortizare" onClick={() => setScheduleTarget(p.row)}>
          <Schedule fontSize="small" />
        </IconButton>
      ),
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Active fixe</Typography>
        <Typography variant="inherit" color="text.primary">Amortizare</Typography>
      </Breadcrumbs>
      <Typography variant="h2">Amortizare active fixe</Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 2 }}>
        <StatCard label={data ? `Amortizare — ${monthLabel(data.currentPeriodFrom)}` : 'Amortizare — luna curentă'}
          value={data ? data.totalCurrentPeriodDepreciation.toFixed(2).replace('.', ',') : '—'} detail="Suma amortizată în perioada curentă, pe tot registrul" />
        <StatCard label={data ? `Amortizare — anul ${new Date(data.yearStart).getFullYear()}` : 'Amortizare — anul curent'}
          value={data ? data.totalYearToDateDepreciation.toFixed(2).replace('.', ',') : '—'} detail="De la 1 ianuarie până azi (YTD), pe tot registrul" />
      </Box>

      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data?.items ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>

      <DepreciationScheduleDialog target={scheduleTarget} onClose={() => setScheduleTarget(null)} />
    </Stack>
  )
}

function StatCard({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="overline" color="text.secondary">{label}</Typography>
        <Typography variant="h3" sx={{ fontFamily: '"IBM Plex Mono", monospace', mt: 0.5 }}>{value}</Typography>
        <Typography variant="caption" color="text.secondary">{detail}</Typography>
      </CardContent>
    </Card>
  )
}

/** Fișă de amortizare pe activ individual, cu scadentar lună cu lună pe toată durata de amortizare. */
function DepreciationScheduleDialog({ target, onClose }: { target: AssetDepreciationSummaryItem | null; onClose: () => void }) {
  const open = target !== null
  const { data } = useQuery({
    queryKey: ['active', 'amortizare', 'scadentar', target?.id ?? null],
    queryFn: () => getAssetDepreciationSchedule(target!.id), enabled: open,
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Fișă de amortizare — {target?.inventoryNumber}</DialogTitle>
      <DialogContent>
        {!data ? (
          <Stack sx={{ py: 4, alignItems: 'center' }}><CircularProgress /></Stack>
        ) : data.commissionedAt === null ? (
          <Typography variant="body2" color="text.secondary">
            Activul nu este încă pus în funcțiune — amortizarea nu a început.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              {data.name} · valoare intrare {data.entryValue.toFixed(2).replace('.', ',')} · valoare reziduală {data.residualValue.toFixed(2).replace('.', ',')}
              {' '}· {data.depreciationDurationMonths} luni, pus în funcțiune la {data.commissionedAt}
            </Typography>
            <Box sx={{ maxHeight: 420, overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Lună</TableCell>
                    <TableCell>Scadență</TableCell>
                    <TableCell align="right">Val. netă deschidere</TableCell>
                    <TableCell align="right">Amortizare perioadă</TableCell>
                    <TableCell align="right">Amortizare cumulată</TableCell>
                    <TableCell align="right">Val. netă închidere</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.entries.map((e) => (
                    <TableRow key={e.periodNumber} sx={{ opacity: e.isElapsed ? 1 : 0.55 }}>
                      <TableCell>{e.periodNumber}</TableCell>
                      <TableCell>{e.periodEnd}</TableCell>
                      <TableCell align="right">{e.openingNetBookValue.toFixed(2).replace('.', ',')}</TableCell>
                      <TableCell align="right">{e.periodDepreciation.toFixed(2).replace('.', ',')}</TableCell>
                      <TableCell align="right">{e.accumulatedDepreciation.toFixed(2).replace('.', ',')}</TableCell>
                      <TableCell align="right">{e.closingNetBookValue.toFixed(2).replace('.', ',')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Rândurile estompate sunt perioade viitoare (proiecție) — nu s-au produs încă.
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Închide</Button>
      </DialogActions>
    </Dialog>
  )
}
